import { graphql, readInlineData } from 'react-relay';
import { GLOBAL_CLIENT_ONLY_LOCALE, handleLocaleUrl } from 'dibs-intl/exports/urls';
import { getCheckoutUrl } from '../helpers/getCheckoutUrl';
import { PRERENDER_CHECKOUT_ROOT_ID } from './constants';
import {
    type PrerenderedCheckoutParams,
    getPrerenderedCheckoutState,
    setPrerenderedCheckoutRootState,
} from './prerenderedCheckoutStore';
import { getCheckoutTriggerPage } from '../helpers/checkoutTrackingTyped';
import { type redirectToPrerenderedCheckout_item$key } from './__generated__/redirectToPrerenderedCheckout_item.graphql';

const SYNTHETIC_PAGE = 'SYNTHETIC_PAGE';

const runPrerenderedCheckoutDeferredActions = (): void => {
    const { deferredActions } = getPrerenderedCheckoutState();
    deferredActions.forEach(action => action());
};

const onPopState = ({ state }: PopStateEvent): void => {
    const isPageSetByHistoryApi = state === SYNTHETIC_PAGE;
    if (!isPageSetByHistoryApi) {
        return;
    }

    // if page was created with history api and opened with back/forward browser buttons
    // then we should reload page, to keep it in sync with url
    window.location.reload();
};

export const handleBrowserButtons = (): void => {
    // function can be called twice, so let's keep only 1 listener
    window.removeEventListener('popstate', onPopState);

    window.addEventListener('popstate', onPopState);
};

function getUtmQueryParams(): Record<string, string> {
    const urlParams = new URLSearchParams(window.location.search);
    return Array.from(urlParams.entries())
        .filter(([key]) => key.startsWith('utm_'))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
}

const itemFragment = graphql`
    fragment redirectToPrerenderedCheckout_item on Item @inline {
        ...getCheckoutUrl_item
    }
`;

export const redirectToPrerenderedCheckout = ({
    item: itemRef,
    params,
    hideParentPage,
    isUserComesFromEmail,
}: {
    item: redirectToPrerenderedCheckout_item$key | null | undefined;
    params: PrerenderedCheckoutParams;
    hideParentPage: () => void;
    isUserComesFromEmail: boolean;
}): void => {
    const item = readInlineData(itemFragment, itemRef);
    const utmParams = getUtmQueryParams();

    const url = getCheckoutUrl({
        item,
        triggerPage: getCheckoutTriggerPage(window.location.href, isUserComesFromEmail),
        ...params,
        ...utmParams,
    });

    const { rootState } = getPrerenderedCheckoutState();
    const checkoutRootElement = window.document.getElementById(PRERENDER_CHECKOUT_ROOT_ID);

    if (rootState !== 'ENABLED_IN_BACKGROUND' || !checkoutRootElement) {
        window.location.assign(handleLocaleUrl(url, GLOBAL_CLIENT_ONLY_LOCALE));
        return;
    }

    hideParentPage();
    // Mark parent page as synthetic to handle back/forward browser buttons
    window.history.replaceState(SYNTHETIC_PAGE, '');
    // Mark new page as synthetic, change url
    window.history.pushState(SYNTHETIC_PAGE, '', handleLocaleUrl(url, GLOBAL_CLIENT_ONLY_LOCALE));
    /**
     * Need to remove overflow:hidden in buyer message center.
     */
    document.body.style.overflow = 'auto';
    setPrerenderedCheckoutRootState('VISIBLE');
    runPrerenderedCheckoutDeferredActions();
};
